import { create } from 'zustand';
import { getCurrentInfo, type ILoadInfo } from '@/service/info';
import { EVENT } from '@/utils/event';

interface ILoadInfoState {
  loadInfo: ILoadInfo | null;
  fetchLoadInfo: () => void;
  setLoadInfo: (info: ILoadInfo | null) => void;
  clearLoadInfo: () => void;
}

function readCachedLoadInfo(): ILoadInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('upload');
    if (!raw) return null;
    const info = JSON.parse(raw) as ILoadInfo;
    return info?.name ? info : null;
  } catch {
    return null;
  }
}

export const useLoadInfoStore = create<ILoadInfoState>((set) => ({
  // Must stay null on SSR + first client paint to match server HTML.
  loadInfo: null,
  fetchLoadInfo: () => {
    const cached = readCachedLoadInfo();
    if (cached) {
      set({ loadInfo: cached });
    }

    getCurrentInfo()
      .then((res) => {
        if (res.data.code === 0 && res.data.data) {
          set({ loadInfo: res.data.data });
          localStorage.setItem('upload', JSON.stringify(res.data.data));
        } else if (res.data.code === 404) {
          dispatchEvent(new Event(EVENT.LOGOUT));
        } else if (cached) {
          set({ loadInfo: cached });
        }
      })
      .catch(() => {
        if (cached) {
          set({ loadInfo: cached });
        }
      });
  },
  setLoadInfo: (info) => set({ loadInfo: info }),
  clearLoadInfo: () => set({ loadInfo: null })
}));

/** Identity display name; only trust loadInfo after client fetch (no sync localStorage read). */
export function getIdentityName(loadInfo: ILoadInfo | null | undefined): string {
  return loadInfo?.name?.trim() || '';
}
